
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { RefreshCw, Smartphone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

declare const __APP_VERSION__: string;
const CURRENT_VERSION: string = __APP_VERSION__;
const DISMISSED_KEY = 'version_check_dismissed';

export function VersionCheckDialog() {
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    setIsIOS(/ipad|iphone|ipod/.test(userAgent) && !(window as any).MSStream);
    checkVersion();
  }, []);

  async function checkVersion() {
    try {
      // Check if user already dismissed this session
      const dismissed = sessionStorage.getItem(DISMISSED_KEY);
      if (dismissed === CURRENT_VERSION) return;

      const { data, error } = await (supabase as any)
        .from('app_versions')
        .select('version_code')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !data) return;

      // Only show update dialog if the DB version is NEWER than current
      if (data.version_code !== CURRENT_VERSION && data.version_code > CURRENT_VERSION) {
        setLatestVersion(data.version_code);
        setOpen(true);
      }
    } catch {
      // Silently fail
    }
  }

  function handleUpdate() {
    if ('caches' in window) {
      caches.keys().then((names: string[]) => {
        return Promise.all(names.map((name: string) => caches.delete(name)));
      }).then(() => {
        location.reload();
      });
    } else {
      location.reload();
    }
  }

  function handleContinue() {
    try {
      sessionStorage.setItem(DISMISSED_KEY, CURRENT_VERSION);
    } catch {}
    setOpen(false);
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="max-w-sm rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-lg">
            <RefreshCw size={20} className="text-primary" />
            Update Available
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm space-y-3">
              <p>A new version is available. Please update for the best experience.</p>
              <span className="text-xs text-muted-foreground block">
                Current: {CURRENT_VERSION} → Latest: {latestVersion}
              </span>

              {/* Icon change notice */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
                <Smartphone size={16} className="text-primary mt-0.5 shrink-0" />
                <div className="text-xs text-foreground space-y-1">
                  <p className="font-medium">🎨 New app icon!</p>
                  <p className="text-muted-foreground">
                    The home screen icon has been updated.
                    {isIOS
                      ? ' On iOS, please remove the app from your home screen and re-add it to see the new icon.'
                      : ' Android will update the icon automatically within a few days.'}
                  </p>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-row gap-2">
          <Button variant="outline" onClick={handleContinue} className="flex-1">
            Continue
          </Button>
          <Button onClick={handleUpdate} className="flex-1">
            Update
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
